/**
 * Panel for attaching / detaching credentials from an asset.
 */

import { useState } from 'react';
import { Key, Link, Unlink } from 'lucide-react';
import { useCredentials, useAttachCredential, useDetachCredential } from '@/hooks/useCredentials';
import type { Asset } from '@/types/asset';

const TYPE_LABELS: Record<string, string> = {
  ssh_password: 'SSH Password',
  ssh_key: 'SSH Key',
  winrm_password: 'WinRM',
  snmp: 'SNMP v1/v2c',
  snmpv3: 'SNMPv3',
};

interface AssetCredentialPanelProps {
  asset: Asset;
}

export default function AssetCredentialPanel({ asset }: AssetCredentialPanelProps) {
  const { data: allCredentials = [] } = useCredentials();
  const attachMutation = useAttachCredential();
  const detachMutation = useDetachCredential();
  const [selectedCred, setSelectedCred] = useState('');

  const attachedIds = new Set(asset.credentials.map((c) => c.id));
  const available = allCredentials.filter((c) => !attachedIds.has(c.id));

  function handleAttach() {
    if (!selectedCred) return;
    attachMutation.mutate(
      { assetId: asset.id, credentialId: selectedCred },
      { onSuccess: () => setSelectedCred('') },
    );
  }

  function handleDetach(credentialId: string) {
    detachMutation.mutate({ assetId: asset.id, credentialId });
  }

  return (
    <div className="space-y-3">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-700">
        <Key className="h-4 w-4" />
        Attached Credentials
      </h3>

      {asset.credentials.length === 0 ? (
        <p className="text-xs text-gray-500">No credentials attached. Scans will run unauthenticated.</p>
      ) : (
        <ul className="space-y-1">
          {asset.credentials.map((cred) => (
            <li
              key={cred.id}
              className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2"
            >
              <div>
                <span className="text-sm font-medium text-gray-800">{cred.name}</span>
                <span className="ml-2 text-xs text-gray-500">{TYPE_LABELS[cred.credential_type] ?? cred.credential_type}</span>
                <span className="ml-2 font-mono text-xs text-gray-400">{cred.username}</span>
              </div>
              <button
                onClick={() => handleDetach(cred.id)}
                disabled={detachMutation.isPending}
                className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                title="Detach credential"
              >
                <Unlink className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {available.length > 0 && (
        <div className="flex items-center gap-2 pt-1">
          <select
            value={selectedCred}
            onChange={(e) => setSelectedCred(e.target.value)}
            className="flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
          >
            <option value="">Select credential to attach…</option>
            {available.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({TYPE_LABELS[c.credential_type] ?? c.credential_type})
              </option>
            ))}
          </select>
          <button
            onClick={handleAttach}
            disabled={!selectedCred || attachMutation.isPending}
            className="flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Link className="h-3.5 w-3.5" />
            Attach
          </button>
        </div>
      )}

      {allCredentials.length === 0 && (
        <p className="text-xs text-gray-400">
          No credentials exist yet. Create them in Admin → Credentials.
        </p>
      )}
    </div>
  );
}
